import { useEffect, useState } from "react";
import { Alert, IconButton, InputAdornment, TextField, Tooltip, Typography, type TextFieldProps } from "@mui/material";
import { InfoOutlined, Sync } from "@mui/icons-material";
import { ConfigDropdownForm } from "@/pages/config/ConfigDropdownForm";
import { useNotification } from "@/context/NotificationContext";
import { useLogger } from "@/hooks/useLogger";
import { LogEventType } from "@/api/log.service";
import { useAuth } from "@/context/AuthContext";
import { getParametros, saveParametros, type Parametros } from "@/api/escala.service";

// Os dois percentuais trafegam com a API como fração (0-1, mesma coluna
// NUMERIC(4,3) do banco) mas são editados aqui como "quantos % folgam", que
// é como appdono/CLAUDE.md e o negócio falam disso ("contingente de 70%" =
// "máximo 30% folgando"). Conversão é só *100 / /100, sem lib nova — o
// arredondamento em paraExibicao evita o resto de ponto flutuante do
// JavaScript (0,29 vira 28.999999999999996 sem ele; R50).
const paraExibicao = (fracao: number) => Math.round(fracao * 1000) / 10;
const paraFracao = (percentual: number) => percentual / 100;

// Teto de int16 (as três constantes abaixo são colunas int16 no banco) — passar disso cai num
// erro genérico de unmarshal do backend em vez de uma mensagem legível (R50).
const MAX_INT16 = 32767;

// Tetos legais que o Method também recusa ultrapassar (appdono/CLAUDE.md):
// OJ 410 do TST (descanso até o 7º dia) e Lei
// 10.101/2000, art. 6º, parágrafo único (domingo ao menos uma vez a cada 3
// semanas no comércio). O client pode configurar qualquer valor de 1 até
// aqui, nunca acima.
const MAX_DIAS_CONSECUTIVOS_LEGAL = 7;
const MAX_DOMINGOS_CONSECUTIVOS_LEGAL = 2;
// Teto de horas por semana da Constituição (art. 7º, XIII).
const MAX_JORNADA_SEMANAL_LEGAL = 44;

// Descrições completas de cada parâmetro, mostradas ao passar o mouse (ou
// focar/tocar) no ícone de informação do campo. Falam do efeito no motor, do
// valor padrão e de quando mexer — não repetem o rótulo.
const DESCRICOES: Record<keyof Parametros, string[]> = {
  perc_max_folga: [
    "Teto de gente folgando ao mesmo tempo num dia de segunda a sábado, como % da equipe daquele dia. Mede folgas, não presença: 30% de folga é o mesmo que \"contingente de 70%\" trabalhando.",
    "O gerador não concede uma folga que passe do teto e a empurra para o dia seguinte. Quem está desligado ou afastado por longo período sai da conta do dia. Feriado em que a loja fecha não passa por este teto.",
    "Padrão: 30%. Quanto menor, mais espalhadas ficam as folgas e mais gente trabalha por dia; um valor muito baixo pode tornar a escala impossível de gerar (a geração é barrada explicando o motivo).",
  ],
  perc_max_folga_domingo: [
    "Teto de gente folgando ao mesmo tempo num domingo, como % da equipe daquele dia. Vale só para domingos; os demais dias usam a folga máxima dos dias úteis.",
    "É aqui que o revezamento quinzenal das mulheres (art. 386 da CLT) esbarra: em média, cada homem precisa folgar em 1 de cada 3 domingos e cada mulher em 1 de cada 2. Com muitas colaboradoras essa necessidade passa do teto — acima de cerca de 40% de mulheres na equipe, nenhuma escala cabe em 40%.",
    "Quando isso acontece a geração é barrada e a tela explica o motivo. Subir este valor é a forma de liberar lojas de maioria feminina, mas vale para todas as lojas deste cliente.",
    "Padrão: 40%.",
  ],
  max_dias_consecutivos: [
    "Quantos dias seguidos alguém pode chegar a trabalhar. Com o valor 7 (o teto legal, OJ 410 do TST: descanso até o 7º dia) ninguém trabalha mais de 6 dias seguidos — a folga vem, no mais tardar, no 7º dia. O validador acusa qualquer sequência que chegue a este número.",
    "O gerador usa o valor para calcular quando cada folga vence. Diminuir aumenta o número de folgas da equipe (com 6, a folga vem no 6º dia); nunca pode passar de 7.",
    "Padrão: 7.",
  ],
  max_domingos_consecutivos: [
    "Quantos domingos seguidos um colaborador pode trabalhar antes de ter que folgar em um. Com o valor 2 (o teto legal para o comércio, Lei 10.101/2000, art. 6º): trabalha 2 domingos, folga no 3º — no mínimo 1 domingo de folga a cada 3. O validador acusa quem passar disso.",
    "Não vale para as colaboradoras: elas seguem o art. 386 da CLT (revezamento quinzenal, no máximo 1 domingo trabalhado seguido), que é fixo e não depende deste campo.",
    "Diminuir aumenta as folgas de domingo dos homens e, com elas, a pressão sobre o teto de folga aos domingos. Nunca pode passar de 2. Padrão: 2.",
  ],
  dias_minimos_ausencia: [
    "Quantos dias um afastamento ou férias precisa durar (sobrepondo o mês) para entrar na escala. Os que atingem o mínimo aparecem como ausência (A) nos dias do intervalo, e a pessoa sai da conta do teto de folga naqueles dias.",
    "Afastamentos mais curtos que isso não alteram a escala: o colaborador continua sendo escalado normalmente.",
    "Padrão: 14 dias.",
  ],
  jornada_semanal: [
    "Quantas horas por semana cada colaborador pode trabalhar. Serve para conferir as horas da semana nas escalas com carga horária; não muda a distribuição das folgas.",
    "Na redução da jornada, baixe para 42 e depois para 40 conforme a fase; vale para todas as lojas deste cliente. Nunca pode passar de 44, o teto da Constituição (art. 7º, XIII).",
    "Padrão: 44 horas.",
  ],
};

// TextField numérico com um ícone de informação no fim do campo: passar o
// mouse (ou focar, ou tocar) abre a descrição completa do parâmetro.
function CampoParametro({
  chave,
  unidade,
  slotProps,
  ...props
}: TextFieldProps & { chave: keyof Parametros; unidade?: string }) {
  const paragrafos = DESCRICOES[chave];
  return (
    <TextField
      type="number"
      fullWidth
      required
      {...props}
      slotProps={{
        ...slotProps,
        input: {
          endAdornment: (
            <InputAdornment position="end">
              {unidade}
              <Tooltip
                arrow
                placement="left"
                enterTouchDelay={0}
                leaveTouchDelay={10000}
                title={
                  <>
                    {paragrafos.map((texto, i) => (
                      <Typography key={i} variant="body2" sx={{ mb: i < paragrafos.length - 1 ? 1 : 0 }}>
                        {texto}
                      </Typography>
                    ))}
                  </>
                }
                slotProps={{ tooltip: { sx: { maxWidth: 380, p: 1.5 } } }}
              >
                <IconButton size="small" edge="end" aria-label={`Sobre ${String(props.label)}`} sx={{ ml: unidade ? 0.5 : 0 }}>
                  <InfoOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ),
        },
      }}
    />
  );
}

export function EscalaParametrosTab() {
  const { user } = useAuth();
  const clientId = user!.loggedClient!.id;
  const { notify } = useNotification();
  const { log } = useLogger();

  const [parametros, setParametros] = useState<Parametros | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [erroCarga, setErroCarga] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    setErroCarga(false);
    getParametros(clientId)
      .then(setParametros)
      .catch(() => {
        setErroCarga(true);
        notify("Erro ao carregar parâmetros", "error");
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  if (isLoading) return <Sync />;
  if (erroCarga || !parametros) {
    return <Alert severity="error">Não foi possível carregar os parâmetros da Escala.</Alert>;
  }

  const handleSave = async () => {
    if (parametros.perc_max_folga <= 0 || parametros.perc_max_folga >= 1) {
      notify("Folga máxima nos dias úteis deve estar entre 0% e 100%", "error");
      return;
    }
    if (parametros.perc_max_folga_domingo <= 0 || parametros.perc_max_folga_domingo >= 1) {
      notify("Folga máxima aos domingos deve estar entre 0% e 100%", "error");
      return;
    }
    if (
      !Number.isInteger(parametros.max_dias_consecutivos) ||
      parametros.max_dias_consecutivos <= 0 ||
      parametros.max_dias_consecutivos > MAX_DIAS_CONSECUTIVOS_LEGAL
    ) {
      notify(`Máximo de dias trabalhados seguidos deve ser um número inteiro entre 1 e ${MAX_DIAS_CONSECUTIVOS_LEGAL}`, "error");
      return;
    }
    if (
      !Number.isInteger(parametros.max_domingos_consecutivos) ||
      parametros.max_domingos_consecutivos <= 0 ||
      parametros.max_domingos_consecutivos > MAX_DOMINGOS_CONSECUTIVOS_LEGAL
    ) {
      notify(
        `Máximo de domingos trabalhados seguidos deve ser um número inteiro entre 1 e ${MAX_DOMINGOS_CONSECUTIVOS_LEGAL}`,
        "error",
      );
      return;
    }
    if (
      !Number.isInteger(parametros.dias_minimos_ausencia) ||
      parametros.dias_minimos_ausencia <= 0 ||
      parametros.dias_minimos_ausencia > MAX_INT16
    ) {
      notify(`Dias mínimos para uma ausência entrar na escala deve ser um número inteiro entre 1 e ${MAX_INT16}`, "error");
      return;
    }
    if (
      !Number.isInteger(parametros.jornada_semanal) ||
      parametros.jornada_semanal <= 0 ||
      parametros.jornada_semanal > MAX_JORNADA_SEMANAL_LEGAL
    ) {
      notify(`Jornada semanal deve ser um número inteiro de horas entre 1 e ${MAX_JORNADA_SEMANAL_LEGAL}`, "error");
      return;
    }

    setIsSaving(true);
    try {
      const result = await saveParametros(parametros, clientId);
      setParametros(result);
      log(LogEventType.Updated, result, "escala_parametro", clientId, "info");
      notify("Parâmetros salvos!", "success");
    } catch (e: any) {
      notify(e?.message || "Erro ao salvar parâmetros", "error");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ConfigDropdownForm
      title="Parâmetros da Escala"
      subtitle="Constantes usadas pelo motor ao gerar a escala — valem para todas as lojas deste cliente."
      onSave={handleSave}
      loading={isSaving}
      defaultOpen
    >
      <CampoParametro
        chave="perc_max_folga"
        unidade="%"
        label="Folga máxima nos dias úteis"
        value={paraExibicao(parametros.perc_max_folga)}
        onChange={(e) =>
          setParametros((p) => p && { ...p, perc_max_folga: paraFracao(Number(e.target.value)) })
        }
        slotProps={{ htmlInput: { min: 1, max: 99, step: 1 } }}
      />
      <CampoParametro
        chave="perc_max_folga_domingo"
        unidade="%"
        label="Folga máxima aos domingos"
        value={paraExibicao(parametros.perc_max_folga_domingo)}
        onChange={(e) =>
          setParametros((p) => p && { ...p, perc_max_folga_domingo: paraFracao(Number(e.target.value)) })
        }
        slotProps={{ htmlInput: { min: 1, max: 99, step: 1 } }}
      />
      <CampoParametro
        chave="max_dias_consecutivos"
        label="Máximo de dias trabalhados seguidos"
        value={parametros.max_dias_consecutivos}
        onChange={(e) =>
          setParametros((p) => p && { ...p, max_dias_consecutivos: Number(e.target.value) })
        }
        slotProps={{ htmlInput: { min: 1, max: MAX_DIAS_CONSECUTIVOS_LEGAL, step: 1 } }}
      />
      <CampoParametro
        chave="max_domingos_consecutivos"
        label="Máximo de domingos trabalhados seguidos"
        value={parametros.max_domingos_consecutivos}
        onChange={(e) =>
          setParametros((p) => p && { ...p, max_domingos_consecutivos: Number(e.target.value) })
        }
        slotProps={{ htmlInput: { min: 1, max: MAX_DOMINGOS_CONSECUTIVOS_LEGAL, step: 1 } }}
      />
      <CampoParametro
        chave="dias_minimos_ausencia"
        label="Dias mínimos para uma ausência entrar na escala"
        value={parametros.dias_minimos_ausencia}
        onChange={(e) =>
          setParametros((p) => p && { ...p, dias_minimos_ausencia: Number(e.target.value) })
        }
        slotProps={{ htmlInput: { min: 1, max: MAX_INT16, step: 1 } }}
      />
      <CampoParametro
        chave="jornada_semanal"
        unidade="h"
        label="Jornada semanal"
        value={parametros.jornada_semanal}
        onChange={(e) =>
          setParametros((p) => p && { ...p, jornada_semanal: Number(e.target.value) })
        }
        slotProps={{ htmlInput: { min: 1, max: MAX_JORNADA_SEMANAL_LEGAL, step: 1 } }}
      />
    </ConfigDropdownForm>
  );
}
